import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { datadogLogs } from '@datadog/browser-logs';

import { useLogoutContext } from '../contexts/AuthenticationWrapper/LogoutContext';
import { STYLOVYZE_FLAGS } from '../types';
import { useIsFeatureEnabled } from '../utils';
import { Events } from '../utils/sharedWorkers.types';
import {
	ExtendSessionPayload,
	ForgeInitPayload,
	getExtendSessionPayloadFromCookies,
	getForgeInitPayloadFromCookies,
	sanitizeSharedWorkerMessageData,
} from './sharedWorkerProviderUtils';
import useRegenerateSharedWorker from './useRegenerateSharedWorker';
import useForgeTokenSyncFromWorker, {
	ForgeTokenUpdatedPayload,
} from './useForgeTokenSyncFromWorker';

export interface SharedWorkerControllerValue {
	workerRef: React.MutableRefObject<SharedWorker | null>;
	expirationTime: string | null;
	showModal: boolean;
	initializeExtendSession: () => void;
	dispatchCloseSession: () => void;
	hasSharedWorkers: boolean;
}

type SharedWorkerEventPayload =
	| Record<string, unknown>
	| number
	| string
	| null;

type SharedWorkerEventHandler = (data: SharedWorkerEventPayload) => void;

type SharedWorkerMessage = {
	data: {
		data: SharedWorkerEventPayload;
		type: Events;
		extraAttributes?: Record<string, unknown>;
	};
};

const ReauthSharedWorkerRefContext = createContext<SharedWorkerControllerValue>(
	{
		workerRef: { current: null },
		expirationTime: null,
		showModal: false,
		initializeExtendSession: () => void 0,
		dispatchCloseSession: () => void 0,
		hasSharedWorkers: false,
	},
);

export const useReauthSharedWorkerProvider = () =>
	React.useContext(ReauthSharedWorkerRefContext);

export interface SharedWorkerProviderProps {
	readonly children: React.ReactNode;
	readonly workerUrl?: string;
	readonly workerName?: string;
	readonly hasSharedWorkers?: boolean;
}

export default function SharedWorkerProvider({
	children,
	workerUrl = '/workers/forge.sharedworker.js',
	workerName = 'forgeWorker',
	hasSharedWorkers = true,
}: SharedWorkerProviderProps) {
	const workerRef = useRef<SharedWorker | null>(null);
	const hasInitializedForgeDataRef = useRef(false);
	const { logout } = useLogoutContext();
	const [expirationTime, setExpirationTime] = useState<string | null>(null);
	const [showModal, setShowModal] = useState(false);
	const resetExpirationTime = useCallback(() => {
		setExpirationTime(null);
	}, []);
	const isRegenerateEnabled = useIsFeatureEnabled(
		STYLOVYZE_FLAGS.REGENERATE_SHARED_WORKER,
	);

	const handleForgeTokenUpdated = useForgeTokenSyncFromWorker();

	const getStringField = useCallback(
		(data: SharedWorkerEventPayload, key: string): string | null => {
			if (typeof data !== 'object' || data === null) return null;
			const value = (data as Record<string, unknown>)[key];
			return typeof value === 'string' ? value : null;
		},
		[],
	);

	// Map worker events to app-side state updates and auth side effects.
	const sharedWorkerEventHandlers: Partial<
		Record<Events, SharedWorkerEventHandler>
	> = useMemo(
		() => ({
			[Events.PROPAGATE_FORGE_TOKEN_UPDATED]: data =>
				handleForgeTokenUpdated(data as ForgeTokenUpdatedPayload),
			[Events.PROPAGATE_SESSION_FINISH_TIME]: data => {
				if (typeof data === 'string') {
					setExpirationTime(data);
				}
			},
			[Events.PROPAGATE_SHOW_MODAL]: data => {
				setShowModal(true);
				const expiresAt = getStringField(data, 'expiresAt');
				if (expiresAt) {
					setExpirationTime(expiresAt);
				}
			},
			[Events.PROPAGATE_SESSION_EXPIRED]: data => {
				setShowModal(false);
				const expiresAt = getStringField(data, 'expiresAt');
				if (expiresAt) {
					setExpirationTime(expiresAt);
				}
			},
			[Events.PROPAGATE_LOGOUT]: () => {
				setShowModal(false);
				resetExpirationTime();
				logout.current?.();
			},
		}),
		[getStringField, handleForgeTokenUpdated, logout, resetExpirationTime],
	);

	// Central worker message handler: sanitize/log payloads and route to handlers.
	const handleMessage = useCallback(
		(event: SharedWorkerMessage) => {
			const { data = {}, type, extraAttributes } = event.data;
			const sanitizedPayload: Record<string, unknown> =
				typeof data === 'object' && data !== null
					? sanitizeSharedWorkerMessageData(
							data as Record<string, unknown>,
					  )
					: { value: data };

			if (type !== Events.DATADOG_LOG) {
				console.debug('Shared Worker response: ', {
					type,
					timestamp: new Date().toISOString(),
					sanitizedData: sanitizedPayload,
				});
			}

			datadogLogs.logger.warn('Shared Worker Message Caught: ' + type, {
				payload: sanitizedPayload as never,
				messageType: type,
				extraAttributes: extraAttributes as Record<string, string>,
			});

			if (type === Events.PING) {
				workerRef.current?.port.postMessage({ type: Events.PONG });
				return;
			}

			if (type) {
				const handler = sharedWorkerEventHandlers[type];
				if (handler) {
					handler(data);
				}
			}
		},
		[sharedWorkerEventHandlers],
	);

	// Create or regenerate the SharedWorker instance.
	const createWorker = useCallback(() => {
		console.count('Creating or regenerating Shared Worker...');
		workerRef.current = new SharedWorker(workerUrl, {
			type: 'module',
			name: workerName,
		});
	}, [workerName, workerUrl]);

	// Initialize worker with Forge session data when available.
	const initForgeData = async () => {
		if (workerRef.current) {
			const forgeInitPayload: ForgeInitPayload | null =
				getForgeInitPayloadFromCookies();

			if (forgeInitPayload) {
				postMessage(Events.INIT_FORGE_DATA, forgeInitPayload);
			}
		}

		await new Promise(resolve => setTimeout(resolve, 0));
	};

	const { checkWorkerHealth, regenerateSharedWorker } =
		useRegenerateSharedWorker(
			workerRef,
			workerUrl,
			workerName,
			handleMessage,
			initForgeData,
			createWorker,
		);

	// Post a message to the worker (with optional health-check/regeneration).
	async function postMessage(type: Events, data?: unknown) {
		if (isRegenerateEnabled) {
			await checkWorkerHealth()
				.then(() => {
					datadogLogs.logger.log(
						'From postMessage: Shared Worker is healthy',
					);
				})
				.catch(err => {
					console.warn(
						'From postMessage: Worker is unresponsive, regenerating...',
						err,
					);
					regenerateSharedWorker();
				});
		}

		workerRef.current?.port.postMessage({ type, data });
	}

	// Retry init until cookies are present after login.
	const tryInitForgeData = useCallback(async () => {
		if (hasInitializedForgeDataRef.current) return;
		const forgeInitPayload: ForgeInitPayload | null =
			getForgeInitPayloadFromCookies();
		if (!forgeInitPayload) return;
		await postMessage(Events.INIT_FORGE_DATA, forgeInitPayload);
		hasInitializedForgeDataRef.current = true;
	}, []);

	// Create the worker and attach message listeners on mount.
	useEffect(() => {
		if (workerUrl) {
			workerRef.current = new SharedWorker(workerUrl, {
				type: 'module',
				name: workerName,
			});
			resetExpirationTime();
			setShowModal(false);
		}

		if (workerRef.current) {
			workerRef.current.port.start();
			workerRef.current.port.addEventListener('message', handleMessage);

			return () => {
				workerRef.current?.port.removeEventListener(
					'message',
					handleMessage,
				);
				workerRef.current?.port.close();
			};
		}
	}, [handleMessage, resetExpirationTime, workerName, workerUrl]);

	// Initial Forge init (async to allow cookies to settle).
	useEffect(() => {
		const timeout = setTimeout(() => initForgeData(), 1000);
		return () => clearTimeout(timeout);
	}, []);

	// Keep retrying Forge init until refresh cookies exist.
	useEffect(() => {
		if (!hasSharedWorkers) return;
		const interval = setInterval(() => {
			void tryInitForgeData();
		}, 1000);
		return () => clearInterval(interval);
	}, [hasSharedWorkers, tryInitForgeData]);

	// Periodic health checks when regeneration is enabled.
	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (isRegenerateEnabled) {
			interval = setInterval(() => {
				checkWorkerHealth()
					.then(() => {
						datadogLogs.logger.log(
							'PING: Shared Worker is healthy',
						);
					})
					.catch(err => {
						console.warn(
							'From PING: Worker is unresponsive, regenerating...',
							err,
						);
						regenerateSharedWorker();
					});
			}, 60000);
		}
		return () => interval && clearInterval(interval);
	}, [checkWorkerHealth, isRegenerateEnabled, regenerateSharedWorker]);

	// Request the worker to extend the session (and reset modal state).
	const initializeExtendSession = () => {
		resetExpirationTime();
		setShowModal(false);
		const payload: ExtendSessionPayload =
			getExtendSessionPayloadFromCookies();
		postMessage(Events.INIT_EXTEND_SESSION, payload);
	};

	const dispatchCloseSession = () => postMessage(Events.DO_LOGOUT);

	const contextValue = useMemo<SharedWorkerControllerValue>(
		() => ({
			workerRef,
			expirationTime,
			showModal,
			initializeExtendSession,
			dispatchCloseSession,
			hasSharedWorkers,
		}),
		[
			expirationTime,
			hasSharedWorkers,
			showModal,
			initializeExtendSession,
			dispatchCloseSession,
		],
	);

	return (
		<ReauthSharedWorkerRefContext.Provider value={contextValue}>
			{children}
		</ReauthSharedWorkerRefContext.Provider>
	);
}
